import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield,
  Lock,
  Server,
  UserCheck,
  FileSearch,
  Key,
  Eye,
  Database,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';

export const HipaaPage = () => {
  const securityFeatures = [
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'All data encrypted in transit (TLS 1.3) and at rest (AES-256).',
    },
    {
      icon: UserCheck,
      title: 'Role-Based Access Control',
      description: 'Granular permissions with Super Admin, Admin, Sales, and Patient roles.',
    },
    {
      icon: FileSearch,
      title: 'Complete Audit Logging',
      description: 'Every action tracked with user, timestamp, IP address, and details.',
    },
    {
      icon: Key,
      title: 'JWT Authentication',
      description: 'Secure token-based authentication with automatic session expiration.',
    },
    {
      icon: Eye,
      title: 'No PHI in URLs',
      description: 'Patient data never exposed in browser history, bookmarks, or server logs.',
    },
    {
      icon: Database,
      title: 'Secure Cloud Storage',
      description: 'iDrive E2 (S3-compatible) with encrypted document storage.',
    },
  ];

  const complianceChecklist = [
    'Business Associate Agreements (BAA) executed with all customers',
    'Administrative safeguards including workforce training',
    'Physical safeguards for data center security',
    'Technical safeguards including encryption and access controls',
    'Regular risk assessments and security audits',
    'Incident response and breach notification procedures',
    'Data backup and disaster recovery plans',
    'Employee security awareness training',
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 mb-6">
                <Shield className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-medium text-teal-700">HIPAA Certified</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Enterprise-Grade HIPAA Compliance
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                DME R'US is built from the ground up with HIPAA compliance at its core. 
                Our platform meets and exceeds the requirements of the HIPAA Security Rule, 
                ensuring your patient data is protected at every level.
              </p>
              <Link to="/demo">
                <Button
                  data-testid="hipaa-demo-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-4"
                >
                  Request Compliance Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="w-64 h-64 bg-teal-50 rounded-full flex items-center justify-center">
                <Shield className="w-32 h-32 text-teal-600" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Security Features
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive security measures to protect your data and ensure compliance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-8 hover:border-teal-600/30 transition-all"
              >
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Checklist */}
      <section className="py-24 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our Compliance Commitment
            </h2>
            <p className="text-lg text-slate-600">
              We implement comprehensive safeguards to protect your data.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-soft">
            <ul className="space-y-4">
              {complianceChecklist.map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BAA Section */}
      <section className="py-24 px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Business Associate Agreement
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            We execute Business Associate Agreements (BAAs) with all customers who process 
            Protected Health Information (PHI) through our platform. Our BAA outlines our 
            responsibilities and obligations under HIPAA.
          </p>
          <Link to="/contact">
            <Button
              data-testid="hipaa-contact-btn"
              className="bg-teal-600 text-white hover:bg-teal-500 rounded-md px-8 py-4 text-lg"
            >
              Request BAA Information
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to See HIPAA Compliance in Action?
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            Schedule a demo focused on our security and compliance features.
          </p>
          <Link to="/demo">
            <Button
              data-testid="hipaa-cta-demo-btn"
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-4 text-lg"
            >
              Schedule Compliance Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HipaaPage;
